import React, { useEffect, useState } from "react";
import API_BASE_URL from "../config";

const FIELD_LABELS = {
  // Common
  status:               "Status",
  // Allocation
  approvedBy:           "Approved By",
  handoverRemarks:      "Handover Remarks",
  expectedReturnDate:   "Expected Return Date",
  returnDate:           "Return Date",
  returnRemarks:        "Return Remarks",
  conditionAtReturn:    "Condition at Return",
  // Transfer
  reasonForTransfer:    "Reason for Transfer",
  dispatchDetails:      "Dispatch Details",
  // Maintenance
  workDone:             "Work Done",
  partsReplaced:        "Parts Replaced",
  repairCost:           "Repair Cost",
  // Audit
  auditName:            "Audit Name",
  // Asset
  assetName:            "Asset Name",
  category:             "Category",
  subCategory:          "Sub-Category",
  brand:                "Brand",
  modelNumber:          "Model Number",
  serialNumber:         "Serial Number",
  colour:               "Colour",
  location:             "Location",
  department:           "Department",
  poNumber:             "PO / Invoice Number",
  grnNumber:            "GRN Number",
  vendorName:           "Vendor / Supplier",
  depreciationMethod:   "Depreciation Method",
  remarks:              "Remarks",
  purchaseDate:         "Purchase Date",
  warrantyStartDate:    "Warranty Start Date",
  warrantyEndDate:      "Warranty End Date",
  purchaseCost:         "Purchase Cost",
  residualValue:        "Residual Value",
  usefulLifeYears:      "Useful Life (Years)",
  // Vendor
  name:                 "Vendor Name",
  contactPerson:        "Contact Person",
  phone:                "Phone",
  email:                "Email",
  gstNumber:            "GST Number",
  address:              "Address",
  vendorType:           "Vendor Type",
  industry:             "Industry",
  website:              "Website",
  currency:             "Currency",
  paymentTerms:         "Payment Terms",
  bankName:             "Bank Name",
  accountHolderName:    "Account Holder Name",
  accountNumber:        "Account Number",
  ifscCode:             "IFSC Code",
  swiftCode:            "SWIFT / BIC Code",
  branchName:           "Branch Name",
  accountType:          "Account Type",
  addresses:            "Addresses",
};

function AddressCell({ value }) {
  const [expanded, setExpanded] = useState(false);

  if (!value || value === "—") return <span className="text-muted">—</span>;

  let parsed = null;
  try { parsed = JSON.parse(value); } catch { /* not JSON */ }

  if (!Array.isArray(parsed)) {
    return <span style={{ wordBreak: "break-word" }}>{value}</span>;
  }

  if (parsed.length === 0) return <span className="text-muted">—</span>;

  return (
    <div>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        style={{ fontSize: 12 }}
        onClick={() => setExpanded(p => !p)}
      >
        {parsed.length} address{parsed.length !== 1 ? "es" : ""} {expanded ? "▴" : "▾"}
      </button>
      {expanded && (
        <div className="mt-1">
          {parsed.map((addr, i) => (
            <div key={i} className="border rounded p-2 mb-1" style={{ fontSize: 11, background: "#f9fafb" }}>
              {addr.addressType && <span className="badge bg-secondary me-1" style={{ fontSize: 10 }}>{addr.addressType}</span>}
              <span>
                {[addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.country, addr.postalCode]
                  .filter(Boolean).join(", ")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ValueCell({ fieldName, value }) {
  if (fieldName === "addresses") return <AddressCell value={value} />;
  if (!value || value === "—") return <span className="text-muted">—</span>;
  return <span style={{ wordBreak: "break-word" }}>{value}</span>;
}

export default function SystemNotes({ entityType, entityId, token }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityType || !entityId || !token) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/system-notes?entityType=${entityType}&entityId=${entityId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setNotes(Array.isArray(data) ? data : []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [entityType, entityId, token]);

  if (loading) return <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>Loading...</div>;

  return (
    <table className="table table-sm align-middle mb-0">
      <thead>
        <tr>
          <th style={{ backgroundColor: "#DFE4EB" }}>Date</th>
          <th style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
          <th style={{ backgroundColor: "#DFE4EB" }}>Action</th>
          <th style={{ backgroundColor: "#DFE4EB" }}>Field</th>
          <th style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
          <th style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
        </tr>
      </thead>
      <tbody>
        {notes.length === 0 ? (
          <tr>
            <td colSpan="6" className="text-muted text-center">No records to show.</td>
          </tr>
        ) : (
          notes.map(n => (
            <tr key={n.id}>
              <td style={{ whiteSpace: "nowrap" }}>
                {n.changedAt ? new Date(n.changedAt).toLocaleString() : "—"}
              </td>
              <td>{n.changedBy || "—"}</td>
              <td>
                <span className={`badge ${n.action === "CREATED" ? "bg-success" : "bg-primary"}`} style={{ fontSize: 11 }}>
                  {n.action}
                </span>
              </td>
              <td>{n.fieldName ? (FIELD_LABELS[n.fieldName] || n.fieldName) : "—"}</td>
              <td className="text-muted"><ValueCell fieldName={n.fieldName} value={n.oldValue ?? "—"} /></td>
              <td><ValueCell fieldName={n.fieldName} value={n.newValue ?? "—"} /></td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
